import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchMovieDetails } from '@/services/tmdb';
import { getCustomVideoUrl } from '@/data/customVideos';
import { Button } from '@/components/ui/button';
import { PageSkeleton } from '@/components/ui/LoadingSkeleton';
import { Play, Download, Share2, ArrowLeft, Star, Clock, Globe, Shield, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const MovieDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [movie, setMovie] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        const loadMovie = async () => {
            setIsLoading(true);
            try {
                const data = await fetchMovieDetails(id);
                // Check for custom video source
                const customUrl = getCustomVideoUrl(data.title, data.year);
                if (customUrl) {
                    data.customSource = customUrl;
                }
                setMovie(data);
            } catch (error) {
                console.error("Error loading movie:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) loadMovie();
    }, [id]);

    if (isLoading) return <PageSkeleton />;
    if (!movie) return <div className="min-h-screen flex items-center justify-center text-white">Película no encontrada</div>;

    const handlePlay = () => {
        if (movie.customSource) {
            setIsPlaying(true);
        } else {
            // For now, if no custom source, maybe open trailer or just log
            window.open(`https://www.youtube.com/watch?v=${movie.trailerKey}`, '_blank');
        }
    };

    return (
        <div className="min-h-screen bg-[#0f172a] text-white flex flex-col pt-24"> {/* Navy Blue BG */}

            {/* Main Split Content */}
            <div className="flex-1 max-w-[1800px] mx-auto w-full px-4 md:px-8 lg:px-12 py-8 flex flex-col lg:flex-row gap-8 items-center lg:items-start justify-center">

                {/* LEFT COLUMN: Poster Card (Reference Style) */}
                <div className="w-full max-w-sm lg:w-[400px] shrink-0">
                    <div className="bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col h-full">
                        {/* Poster Image */}
                        <div className="relative aspect-[2/3] w-full">
                            <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" />
                        </div>

                        {/* Card Footer with Button */}
                        <div className="p-4 bg-white flex flex-col gap-4">
                            {/* Logo/Title Area (Simulating logo if text) */}
                            <h1 className="text-3xl font-black text-black text-center tracking-tighter leading-none uppercase">
                                {movie.title}
                            </h1>

                            {/* Red Primary Button pinned to bottom */}
                            <Button
                                onClick={handlePlay}
                                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-bold text-lg rounded-md shadow-md transition-all"
                            >
                                {isPlaying ? 'Viendo Ahora' : 'Ver Película'}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: Video/Backdrop Area (Reference Style) */}
                <div className="flex-1 w-full h-full flex flex-col gap-6">

                    {/* Backdrop / Video Player */}
                    <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl group bg-black border border-white/10">
                        {isPlaying && movie.customSource ? (
                            <div className="absolute inset-0 z-20">
                                <iframe
                                    src={movie.customSource}
                                    className="w-full h-full"
                                    frameBorder="0"
                                    allowFullScreen
                                    allow="autoplay; encrypted-media"
                                    title={movie.title}
                                ></iframe>
                                {/* Close Button */}
                                <button
                                    onClick={() => setIsPlaying(false)}
                                    className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-red-600 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        ) : (
                            <div className="absolute inset-0 cursor-pointer" onClick={handlePlay}>
                                <div
                                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                    style={{ backgroundImage: `url(${movie.backdrop})` }}
                                />
                                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />

                                {/* Centered Play Button */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/40 group-hover:scale-110 transition-transform duration-300 shadow-[0_0_50px_rgba(255,255,255,0.3)]">
                                        <Play className="w-10 h-10 text-white fill-white ml-1" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Movie Metadata (Below Video) */}
                    <div className="space-y-4 px-2">
                        <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-gray-300">
                            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                                {movie.title} <span className="text-gray-500 font-normal">({movie.year})</span>
                            </h2>
                        </div>

                        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                            <span className="flex items-center gap-1.5 px-2 py-0.5 border border-gray-600 rounded text-xs uppercase font-bold text-gray-300">
                                {movie.adult ? 'R' : 'PG-13'}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                <span className="text-white font-bold">{movie.rating}</span>
                                <span>({movie.votes} votos)</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Clock className="w-4 h-4" />
                                {movie.duration}
                            </span>

                            {/* Incentive Genre Tags */}
                            <div className="flex items-center gap-2">
                                {movie.genres.map((g, i) => (
                                    <span
                                        key={i}
                                        className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                                    >
                                        {g}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <p className="text-lg text-gray-300 leading-relaxed max-w-4xl">
                            {movie.description}
                        </p>

                        {/* Secondary Actions */}
                        <div className="flex items-center gap-4 pt-2">
                            <Button variant="outline" className="gap-2 border-gray-600 text-gray-300 hover:text-white hover:bg-white/10">
                                <Share2 className="w-4 h-4" /> Compartir
                            </Button>
                            <Button variant="outline" className="gap-2 border-gray-600 text-gray-300 hover:text-white hover:bg-white/10">
                                <Download className="w-4 h-4" /> Download
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cast Section (Bottom) */}
            <div className="w-full max-w-[1800px] mx-auto px-4 md:px-12 py-12 border-t border-gray-800 mt-8">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-white">
                    <span className="w-1 h-6 bg-red-600 rounded-full block"></span>
                    ELENCO PRINCIPAL
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                    {movie.cast.map((actor, idx) => (
                        <div key={idx} className="group cursor-pointer" onClick={() => navigate(`/person/${actor.id}`)}>
                            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-gray-800 mb-2 relative">
                                {actor.photo ? (
                                    <img src={actor.photo} alt={actor.name} className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">No Image</div>
                                )}
                            </div>
                            <p className="text-sm font-bold text-gray-200 truncate">{actor.name}</p>
                            <p className="text-xs text-gray-500 truncate">{actor.character}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MovieDetailPage;
